/**
 * userDisplay.js — Helpers pour afficher proprement le nom/email d'un user.
 *
 * Cas particulier Apple Sign-In : si l'utilisateur a choisi "Masquer mon
 * email", Apple génère un alias `xxx@privaterelay.appleid.com`. Cet alias
 * ne doit PAS être affiché tel quel dans l'UI — on préfère afficher son
 * vrai nom (fourni à la 1ère connexion via `firstName`/`lastName`) ou
 * un placeholder neutre.
 */

const APPLE_RELAY_DOMAIN = '@privaterelay.appleid.com';

/**
 * Renvoie true si la chaîne est un email Apple Relay (anonyme).
 */
export function isAppleRelayEmail(value) {
  if (!value || typeof value !== 'string') return false;
  return value.toLowerCase().endsWith(APPLE_RELAY_DOMAIN);
}

/**
 * Renvoie true si la chaîne ressemble à une adresse email.
 */
function looksLikeEmail(value) {
  if (!value || typeof value !== 'string') return false;
  return /\S+@\S+\.\S+/.test(value);
}

/**
 * Récupère un nom complet présentable.
 * Ordre de priorité :
 *  1. firstName + lastName (combinés)
 *  2. firstName seul
 *  3. lastName seul
 *  4. name (si pas un email)
 *  5. partie locale de l'email (avant le @) si email NON-relay
 *  6. "Mon profil" si email relay Apple ou rien
 */
export function getFullName(user, fallback = 'Mon profil') {
  if (!user) return fallback;

  const fn = (user.firstName || '').trim();
  const ln = (user.lastName  || '').trim();
  if (fn && ln) return `${fn} ${ln}`;
  if (fn) return fn;
  if (ln) return ln;

  const n = (user.name || '').trim();
  if (n && !looksLikeEmail(n)) return n;

  const email = (user.email || '').trim();
  if (email && !isAppleRelayEmail(email)) {
    // Vrai email → on extrait la partie locale (avant @) capitalisée
    const local = email.split('@')[0];
    if (local && local.length >= 2) {
      return local
        .replace(/[._-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    }
  }

  return fallback;
}

/**
 * Prénom uniquement (pour les saluts type "Bonjour Anaïs").
 * Ne renvoie jamais d'email.
 */
export function getFirstName(user, fallback = 'Invité') {
  if (!user) return fallback;
  const fn = (user.firstName || '').trim();
  if (fn) return fn;

  const n = (user.name || '').trim();
  if (n && !looksLikeEmail(n)) {
    return n.split(' ')[0];
  }

  const email = (user.email || '').trim();
  if (email && !isAppleRelayEmail(email)) {
    const local = email.split('@')[0];
    if (local && local.length >= 2) {
      // Capitalize 1ère lettre
      return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
    }
  }

  return fallback;
}

/**
 * Email à afficher (peut être null si email relay Apple → on cache).
 * Pour les écrans où l'email est secondaire/optionnel.
 */
export function getDisplayEmail(user) {
  if (!user) return null;
  const email = (user.email || '').trim();
  if (!email) return null;
  if (isAppleRelayEmail(email)) return null;  // on n'affiche pas l'alias relais
  return email;
}

/**
 * Email à afficher TOUJOURS (même relay), mais raccourci si trop long.
 * Utilisé quand l'email DOIT être visible (settings, debug).
 */
export function getRawEmail(user) {
  if (!user) return '';
  return (user.email || '').trim();
}

/**
 * Calcule l'âge d'un user à partir de sa birthDate (format AAAA-MM-JJ).
 * Retourne null si birthDate manquante ou invalide.
 */
export function computeAge(user) {
  if (!user?.birthDate) return null;
  const m = String(user.birthDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const birth = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const md = now.getMonth() - birth.getMonth();
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age--;
  if (age < 0 || age > 130) return null;
  return age;
}

/**
 * Renvoie le groupe d'âge d'un user sur une échelle 1 à 5.
 *
 *   1 = 18-25 ans (très jeune, tendance forte)
 *   2 = 25-35 ans (jeune adulte, mode urbaine)
 *   3 = 35-50 ans (adulte, élégance accessible)
 *   4 = 50-65 ans (mature, sobriété raffinée)
 *   5 = 65+ ans  (senior, classiques confortables)
 *
 * Retourne null si âge inconnu.
 */
export function getAgeGroup(user) {
  const age = computeAge(user);
  if (age == null) return null;
  if (age < 25) return 1;
  if (age < 35) return 2;
  if (age < 50) return 3;
  if (age < 65) return 4;
  return 5;
}

/**
 * Conversion inverse : tranche d'âge symbolique pour affichage.
 */
export function ageGroupLabel(group) {
  switch (group) {
    case 1: return '18-25 ans';
    case 2: return '25-35 ans';
    case 3: return '35-50 ans';
    case 4: return '50-65 ans';
    case 5: return '65+ ans';
    default: return '';
  }
}

export default {
  getFullName,
  getFirstName,
  getDisplayEmail,
  getRawEmail,
  isAppleRelayEmail,
  computeAge,
  getAgeGroup,
  ageGroupLabel,
};
